import * as React from "react"
import { useState, useEffect, useCallback, useRef } from "react"
import {
    Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
    DialogTitle, Divider, FormControlLabel, LinearProgress, MenuItem, Switch,
    TextField, Typography
} from "@mui/material"
import { IconSeeker } from "@ui-seeker/core"
import { useAppToast } from "@ui-seeker/app"
import { getSubmit, postSubmit } from "@ui-seeker/utils"

import { BACKEND_APIS } from "@constants"

const MODELS = [
    { value: 'gpt-5.6-luna', label: 'GPT-5.6 Luna (recommended)' },
    { value: 'gpt-5.1', label: 'GPT-5.1' },
]

const POLL_MS = 5000

// Batch-mode run states: the OpenAI Batch queue sits between 'running' and
// 'finished' (build → wait → ingest, twice: picks and verify). They all count
// as in-flight, so a second launch stays blocked while any of them is set.
const QUEUED_STATES = {
    building: 'preparing the batch file',
    waiting_picks: 'queued at OpenAI · picks',
    ingesting_picks: 'reading the pick results',
    waiting_verify: 'queued at OpenAI · verify',
    ingesting_verify: 'reading the verify results',
}
const ACTIVE_STATES = ['running', 'applying', ...Object.keys(QUEUED_STATES)]
// `stalled`: the normalizer's sweep gave up on a batch-mode run after 3 revives
// (869fe2zfr). Not active (a new launch is allowed), cancellable, and the only
// state where "Resume" is offered — one press per 30 min, attributed server-side.
const CANCELLABLE_STATES = ['running', 'stalled', ...Object.keys(QUEUED_STATES)]
const RESUME_COOLDOWN_MS = 30 * 60 * 1000

const resumeCooldownLeft = (run) => {
    const log = run.params?.resume_log
    if (!log?.length) return 0
    const last = Date.parse(log[log.length - 1].at)
    return Number.isNaN(last) ? 0 : Math.max(0, last + RESUME_COOLDOWN_MS - Date.now())
}

const appliedTotal = (run) => (run.applied_validate || 0) + (run.applied_not_found || 0)
    + (run.applied_raced || 0) + (run.applied_failed || 0)

function RunSummary({ run }) {
    const pct = run.total_tasks > 0 ? Math.round((run.evaluated / run.total_tasks) * 100) : 0
    const running = run.status === 'running'
    const applying = run.status === 'applying'
    const queued = QUEUED_STATES[run.status]
    const stalled = run.status === 'stalled'
    const applyTarget = (run.n_validate || 0) + (run.n_not_found || 0)
    const applyPct = applyTarget > 0 ? Math.round((appliedTotal(run) / applyTarget) * 100) : 0
    return (
        <Box sx={{ border: theme => `1px solid ${theme.palette.divider}`, borderRadius: 1, p: 1.5, mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Chip size="small" label={`Run #${run.id_run}`} />
                <Chip size="small" color={(running || applying || queued) ? 'info' : run.status === 'finished' ? 'success' : stalled ? 'error' : 'default'}
                      label={run.status} />
                <Chip size="small" variant="outlined" label={run.dry_run ? 'dry-run' : 'apply'} />
                {run.params?.execution_mode === 'batch' && (
                    <Chip size="small" variant="outlined" color="info" label="batch queue" />
                )}
                <Typography variant="caption" color="text.secondary">
                    {run.params?.model} · thr {run.params?.threshold}
                    {run.params?.nf_threshold != null ? ` · nf ${run.params.nf_threshold}` : ' · nf off'}
                </Typography>
            </Box>
            {stalled && (
                <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                    Stuck in "{run.params?.stalled_from}" — the automatic sweep gave up after {run.params?.reap_count ?? 3} revives.
                    Resume re-runs that stage once; if it stalls again, cancel and relaunch.
                </Typography>
            )}
            {running && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                    <LinearProgress variant="determinate" value={pct} sx={{ flex: 1, height: 8, borderRadius: 4 }} />
                    <Typography variant="caption">{run.evaluated}/{run.total_tasks}</Typography>
                </Box>
            )}
            {queued && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                    <LinearProgress sx={{ flex: 1, height: 8, borderRadius: 4 }} />
                    <Typography variant="caption">{queued}</Typography>
                </Box>
            )}
            {applying && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                    <LinearProgress variant="determinate" color="success" value={applyPct}
                                    sx={{ flex: 1, height: 8, borderRadius: 4 }} />
                    <Typography variant="caption">applying {appliedTotal(run)}/{applyTarget}</Typography>
                </Box>
            )}
            <Box sx={{ display: 'flex', gap: 1.5, mt: 1, flexWrap: 'wrap' }}>
                <Typography variant="caption"><b>{run.n_validate}</b> validate</Typography>
                <Typography variant="caption"><b>{run.n_not_found}</b> not found</Typography>
                <Typography variant="caption"><b>{run.n_sampled}</b> control sample</Typography>
                <Typography variant="caption"><b>{run.n_qa}</b> for QA</Typography>
                {run.n_error > 0 && (
                    <Typography variant="caption" color="error.main"><b>{run.n_error}</b> errors</Typography>
                )}
            </Box>
            {(run.applied_validate > 0 || run.applied_not_found > 0 || run.applied_failed > 0) && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    applied: {run.applied_validate} validated · {run.applied_not_found} not found
                    {run.applied_raced > 0 ? ` · ${run.applied_raced} raced (QA first)` : ''}
                    {run.applied_failed > 0 ? ` · ${run.applied_failed} failed` : ''}
                </Typography>
            )}
        </Box>
    )
}

export default function SecondPassDialog({ target, onClose, onApplied }) {
    const { showToast } = useAppToast()
    const batchID = target?.batchID

    const [model, setModel] = useState('gpt-5.6-luna')
    const [threshold, setThreshold] = useState(0.8)
    const [nfEnabled, setNfEnabled] = useState(true)
    const [nfThreshold, setNfThreshold] = useState(0.9)
    const [samplePct, setSamplePct] = useState(0)
    const [limit, setLimit] = useState('')
    const [dryRun, setDryRun] = useState(true)
    const [verifyImages, setVerifyImages] = useState(true)
    const [speedMode, setSpeedMode] = useState(false)   // OFF = OpenAI Batch API

    const [runs, setRuns] = useState([])
    const [busy, setBusy] = useState(false)
    const pollRef = useRef(null)

    const load = useCallback(() => {
        if (!batchID) return
        getSubmit(`${BACKEND_APIS.BATCH_ADMIN}/admin/batches/${batchID}/second-pass`)
            .then(res => setRuns(res?.runs || []))
            .catch(() => {})
    }, [batchID])

    // Poll while the dialog is open and something is running
    useEffect(() => {
        if (!batchID) return undefined
        load()
        pollRef.current = setInterval(load, POLL_MS)
        return () => clearInterval(pollRef.current)
    }, [batchID, load])

    const latest = runs[0]
    const hasRunning = runs.some(r => ACTIVE_STATES.includes(r.status))
    // Remaining = decisions not successfully applied; 'failed' ones count as
    // remaining so the button offers a retry (e.g. after a fixed bug).
    const appliedDone = latest
        ? (latest.applied_validate || 0) + (latest.applied_not_found || 0) + (latest.applied_raced || 0)
        : 0
    const applyRemaining = latest
        ? Math.max(0, (latest.n_validate || 0) + (latest.n_not_found || 0) - appliedDone)
        : 0
    const applyCandidate = latest && latest.status === 'finished' && applyRemaining > 0

    const launch = async () => {
        setBusy(true)
        try {
            const res = await postSubmit(`${BACKEND_APIS.BATCH_ADMIN}/admin/batches/${batchID}/second-pass`, {
                model,
                threshold: Number(threshold),
                nfThreshold: nfEnabled ? Number(nfThreshold) : null,
                sampleRate: Number(samplePct) / 100,
                limit: limit ? Number(limit) : null,
                dryRun,
                verifyImages,
                // Transport: OpenAI Batch queue by default, immediate run in speed mode
                executionMode: speedMode ? 'sync' : 'batch',
            })
            if (res?.success === false) throw new Error(res?.error)
            showToast.success(`AI pass launched (${res?.run?.total_tasks} tasks${res?.run?.dry_run ? ', dry-run' : ''})`
                + (speedMode ? '' : ' — queued at OpenAI, results within 24h'))
            load()
        } catch (e) {
            showToast.error(e?.message || 'Error launching AI pass')
        } finally {
            setBusy(false)
        }
    }

    const applyRun = async (idRun) => {
        setBusy(true)
        try {
            const res = await postSubmit(`${BACKEND_APIS.BATCH_ADMIN}/admin/second-pass/run/${idRun}/apply`, {})
            if (res?.success === false) throw new Error(res?.error)
            showToast.success(`Applying ${res.enqueued} decisions in background — progress below`)
            load()
            onApplied && onApplied(batchID)
        } catch (e) {
            showToast.error(e?.message || 'Error applying decisions')
        } finally {
            setBusy(false)
        }
    }

    const resumeRun = async (idRun) => {
        try {
            const res = await postSubmit(`${BACKEND_APIS.BATCH_ADMIN}/admin/second-pass/run/${idRun}/resume`, { idBatch: batchID })
            showToast.success(`Run resumed from ${res?.stage || 'its last stage'}`)
            load()
        } catch (e) {
            showToast.error(e?.response?.data?.error || 'Error resuming run')
        }
    }

    const cancelRun = async (idRun) => {
        try {
            await postSubmit(`${BACKEND_APIS.BATCH_ADMIN}/admin/second-pass/run/${idRun}/cancel`, {})
            showToast.success('Run cancelled (queued tasks will be skipped)')
            load()
        } catch (e) {
            showToast.error('Error cancelling run')
        }
    }

    return (
        <Dialog open={!!target} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>
                AI Second Pass — Batch #{batchID}{target?.batchName ? ` · ${target.batchName}` : ''}
            </DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Re-evaluates the batch's pending-QA tasks with the benchmarked AI matcher:
                    confident matches are validated, confident not-founds closed, everything
                    ambiguous stays for QA. A control sample of auto decisions is always left
                    for human review.
                </Typography>

                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 1 }}>
                    <TextField select size="small" label="Model" value={model}
                               onChange={e => setModel(e.target.value)} sx={{ minWidth: 230 }}>
                        {MODELS.map(m => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
                    </TextField>
                    <TextField size="small" type="number" label="Validate threshold" value={threshold}
                               onChange={e => setThreshold(e.target.value)}
                               inputProps={{ min: 0.5, max: 1, step: 0.05 }} sx={{ width: 150 }}
                               helperText="min pick confidence" />
                </Box>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', mb: 1 }}>
                    <FormControlLabel control={
                        <Switch size="small" checked={nfEnabled} onChange={e => setNfEnabled(e.target.checked)} />
                    } label={<Typography variant="body2">Auto not-found</Typography>} />
                    <TextField size="small" type="number" label="Not-found threshold" value={nfThreshold}
                               onChange={e => setNfThreshold(e.target.value)} disabled={!nfEnabled}
                               inputProps={{ min: 0.5, max: 1, step: 0.05 }} sx={{ width: 160 }}
                               helperText="min no-match certainty" />
                    <TextField size="small" type="number" label="Control sample %" value={samplePct}
                               onChange={e => setSamplePct(e.target.value)}
                               inputProps={{ min: 0, max: 100, step: 1 }} sx={{ width: 140 }}
                               helperText="left for QA" />
                    <TextField size="small" type="number" label="Limit" value={limit}
                               onChange={e => setLimit(e.target.value)} placeholder="all"
                               sx={{ width: 100 }} helperText="max tasks" />
                </Box>
                <FormControlLabel control={
                    <Switch checked={verifyImages} onChange={e => setVerifyImages(e.target.checked)} />
                } label={
                    <Typography variant="body2">
                        Use photos in verification (turn OFF for sites that reuse one photo for
                        all shades — e.g. Druni — where photo color wrongly vetoes exact shade matches)
                    </Typography>
                } />
                <FormControlLabel control={
                    <Switch checked={dryRun} onChange={e => setDryRun(e.target.checked)} />
                } label={
                    <Typography variant="body2">
                        Dry-run first (evaluate and report — apply the decisions afterwards)
                    </Typography>
                } />
                {/* Transport: cheap OpenAI-Batch queue by default, immediate per-row run when urgent */}
                <FormControlLabel control={
                    <Switch checked={speedMode} onChange={e => setSpeedMode(e.target.checked)} />
                } label={
                    <Box>
                        <Typography variant="body2">Speed mode — run it immediately</Typography>
                        <Typography variant="caption" color="text.secondary">
                            {speedMode
                                ? 'Results in minutes · full price'
                                : 'Off: OpenAI Batch queue · ~50% cheaper · results within 24h (you can close this window)'}
                        </Typography>
                    </Box>
                } />

                {runs.length > 0 && (
                    <>
                        <Divider sx={{ my: 2 }} />
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>Runs</Typography>
                        {runs.map(run => (
                            <Box key={run.id_run}>
                                <RunSummary run={run} />
                                <Box sx={{ display: 'flex', gap: 1, mb: 1.5, mt: -0.5 }}>
                                    {CANCELLABLE_STATES.includes(run.status) && (
                                        <Button size="small" color="error" onClick={() => cancelRun(run.id_run)}>
                                            Cancel
                                        </Button>
                                    )}
                                    {run.status === 'stalled' && (
                                        <Button size="small" variant="outlined" color="warning" disabled={busy || resumeCooldownLeft(run) > 0}
                                                startIcon={<IconSeeker icon="rotate-right" />}
                                                onClick={() => resumeRun(run.id_run)}>
                                            {resumeCooldownLeft(run) > 0
                                                ? `Resume (wait ${Math.ceil(resumeCooldownLeft(run) / 60000)} min)`
                                                : 'Resume'}
                                        </Button>
                                    )}
                                    {run.id_run === latest?.id_run && applyCandidate && (
                                        <Button size="small" variant="contained" color="success" disabled={busy}
                                                startIcon={<IconSeeker icon="circle-check" />}
                                                onClick={() => applyRun(run.id_run)}>
                                            Apply {applyRemaining} decisions
                                        </Button>
                                    )}
                                </Box>
                            </Box>
                        ))}
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
                <Button variant="contained" disabled={busy || hasRunning}
                        startIcon={busy ? <CircularProgress size={16} /> : <IconSeeker icon="robot" />}
                        onClick={launch}>
                    {dryRun ? 'Launch dry-run' : 'Launch & apply'}
                </Button>
            </DialogActions>
        </Dialog>
    )
}
